import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STATUS_STYLE } from "@/components/PackRequests";
import { api, errMsg, fmtDate } from "@/lib/api";

const RequestRow = ({ r, onChanged, onCreatePack }) => {
  const [note, setNote] = useState(r.admin_note);
  const update = async (status, admin_note = note) => {
    try {
      await api.put(`/admin/pack-requests/${r.id}`, { status, admin_note });
      toast.success(`Request marked ${status}`);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="grid gap-4 p-6 lg:grid-cols-12" data-testid={`admin-pack-request-${r.id}`}>
      <div className="space-y-1 lg:col-span-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-display text-lg font-bold">{r.textbook_title}</span>
          <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${STATUS_STYLE[r.status]}`}>{r.status}</span>
        </div>
        <div className="text-sm text-slate-600">{[r.edition, r.subject].filter(Boolean).join(" · ") || "No edition or subject given"}</div>
        {r.glossary_url && <a href={r.glossary_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 break-all text-sm text-blue-700 hover:underline">{r.glossary_url}<ExternalLink className="h-3 w-3 shrink-0" /></a>}
        {r.notes && <p className="text-sm text-slate-700">“{r.notes}”</p>}
        <div className="text-xs text-slate-500" data-testid={`admin-pack-request-requester-${r.id}`}>{[r.requester_name, r.requester_email].filter(Boolean).join(" · ") || "Anonymous"} · {r.requester_role} · {fmtDate(r.created_at)}</div>
      </div>
      <div className="space-y-2 lg:col-span-4">
        <Input data-testid={`admin-pack-request-note-${r.id}`} placeholder="Reply to requester (optional)" value={note} onChange={(e) => setNote(e.target.value)} onBlur={() => note !== r.admin_note && update(r.status)} className="bg-white" />
      </div>
      <div className="flex flex-wrap items-start gap-2 lg:col-span-3 lg:justify-end">
        <Select value={r.status} onValueChange={(s) => update(s)}>
          <SelectTrigger className="h-9 w-32 bg-white" data-testid={`admin-pack-request-status-${r.id}`}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{Object.keys(STATUS_STYLE).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => onCreatePack(r)} data-testid={`admin-pack-request-create-${r.id}`}>
          <Plus className="mr-1 h-3.5 w-3.5" />Create pack
        </Button>
        <Button size="icon" variant="ghost" title="Delete request" data-testid={`admin-pack-request-delete-${r.id}`}
          onClick={async () => { if (window.confirm("Delete this request?")) { await api.delete(`/admin/pack-requests/${r.id}`); toast.success("Request deleted"); onChanged(); } }}>
          <Trash2 className="h-4 w-4 text-red-600" />
        </Button>
      </div>
    </div>
  );
};

export const PackRequestsAdmin = ({ onCreatePack, refreshKey }) => {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("Open");
  const load = useCallback(() => api.get("/admin/pack-requests").then((r) => setRequests(r.data)), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  const shown = requests.filter((r) => filter === "All" || r.status === filter);

  return (
    <section className="space-y-4" data-testid="admin-pack-requests-section">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-bold">Pack requests</h2>
          <p className="text-sm text-slate-600">Textbooks students and instructors have asked for. "Create pack" starts a Draft with their details filled in.</p>
        </div>
        <div className="flex gap-2">
          {["Open", "Planned", "Added", "Declined", "All"].map((f) => (
            <button key={f} data-testid={`admin-pack-requests-filter-${f.toLowerCase()}`} onClick={() => setFilter(f)}
              className={`rounded-full border px-3 py-1 text-sm ${filter === f ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white"}`}>
              {f} ({f === "All" ? requests.length : requests.filter((r) => r.status === f).length})
            </button>
          ))}
        </div>
      </div>
      <div className="divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {shown.length === 0
          ? <p className="p-8 text-center text-sm text-slate-500" data-testid="admin-pack-requests-empty">No {filter === "All" ? "" : filter.toLowerCase() + " "}requests.</p>
          : shown.map((r) => <RequestRow key={r.id + r.status + r.admin_note} r={r} onChanged={load} onCreatePack={onCreatePack} />)}
      </div>
    </section>
  );
};
